<script lang="ts" setup>
import { formatDate, formatISO } from 'date-fns';
import PrimeTag from 'primevue/tag';

const runtimeConfig = useRuntimeConfig();

useSeoMeta({
  title: 'Blog - Vismo',
  description: 'Vismo is a geospatial data platform that brings your projects to life',
  ogTitle: 'Blog - Vismo',
  ogDescription: 'Vismo is a geospatial data platform that brings your projects to life',
  ogImage: joinPath(runtimeConfig.public.siteUrl, 'og.png'),
  ogUrl: joinPath(runtimeConfig.public.siteUrl, '/blog'),
  twitterTitle: 'Blog - Vismo',
  twitterDescription: 'Vismo is a geospatial data platform that brings your projects to life',
  twitterCard: 'summary_large_image',
  twitterImage: joinPath(runtimeConfig.public.siteUrl, 'twitter.png'),
});

const { data: blogs } = await useAsyncData('blogs', () =>
  queryCollection('blog').order('date', 'DESC').all(),
);
</script>

<template>
  <div class="blog-index">
    <section class="blog-index__content">
      <h1 class="blog-index__content-title">Blog 📔</h1>

      <div class="blog-index__content-list">
        <article v-for="blog in blogs" :key="blog.id" class="blog-index__article">
          <NuxtLink :to="`/blog/${blog.slug}`">
            <NuxtImg
              class="blog-index__article-image"
              :src="blog.image"
              :alt="blog.imageAlt || `Blog image for '${blog.title}'`"
            />
          </NuxtLink>
          <h2 class="blog-index__article-title">
            <NuxtLink :to="`/blog/${blog.slug}`">{{ blog.title }}</NuxtLink>
          </h2>
          <div class="blog-index__article-meta">
            <BlogAvatar :who="blog.who" rel="author" />
            <time class="blog-index__article-date" :datetime="formatISO(blog.date)">{{
              formatDate(blog.date, 'MMMM dd, yyyy')
            }}</time>
          </div>
          <ul class="blog-index__article-tags">
            <li v-for="tag in blog.tags" :key="tag"><PrimeTag :value="tag" /></li>
          </ul>
          <p class="blog-index__article-description">
            {{ blog.description.replaceAll('\\n', ' ') }}
          </p>
        </article>
      </div>
    </section>

    <MailingList
      title="Stay up to date with our latest blog posts."
      subtitle="Join our mailing list to get the latest updates."
    />
  </div>
</template>

<style scoped lang="scss">
@use '../../assets/styles/_globals.scss' as g;

.blog-index {
  display: flex;
  flex-direction: column;
  gap: 5rem;
  overflow: hidden;

  &__content {
    display: flex;
    flex-direction: column;
    gap: 3rem;

    margin: 0 11rem;

    @media (max-width: g.$desktop-breakpoint) {
      margin: 0 6rem;
    }

    @media (max-width: g.$mobile-breakpoint) {
      margin: 2rem;
    }
  }

  &__content-title {
    font-size: 2.3rem;
    font-weight: 800;
    text-align: center;
  }

  &__content-list {
    display: grid;
    gap: 3rem;
    grid-template-columns: repeat(auto-fill, minmax(23rem, 1fr));

    @media (max-width: g.$mobile-breakpoint) {
      grid-template-columns: 1fr;
    }
  }

  &__article {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  &__article-image {
    width: 100%;
    height: 220px;
    object-fit: cover;
    border-radius: 0.5rem;

    @media (max-width: g.$mobile-breakpoint) {
      height: 110px;
    }
  }

  &__article-title {
    font-size: 1.5rem;
    font-weight: 800;

    @media (max-width: g.$mobile-breakpoint) {
      font-size: 1.2rem;
    }
  }

  &__article-meta {
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
    align-items: center;
  }

  &__article-date {
    font-size: 1rem;
    font-weight: 700;
  }

  &__article-tags {
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
  }

  &__article-description {
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.4;
  }
}
</style>
